/* Six Cards Carousel — dependency-free
   - 6 slides, 4 per page desktop, 2 tablet, 1 mobile
   - Dots + prev/next; keyboard support
   - Idempotent init + MutationObserver
   - EXTRA: capture-phase “portal” so modals opened from this slider are always centered
*/
(function () {
  'use strict';

  const SEL_SECTION = '[data-sixcards]';

  function perView() {
    if (window.matchMedia('(max-width: 640px)').matches) return 1;
    if (window.matchMedia('(max-width: 1024px)').matches) return 2;
    return 4;
  }

  /* ---- tiny portal helpers, local to this file ---- */
  function portalize(dialog, toBody) {
    if (!dialog) return;
    if (toBody) {
      if (dialog._owner) return;        // already portaled
      dialog._owner = dialog.parentNode;
      dialog._next  = dialog.nextSibling;
      document.body.appendChild(dialog);
    } else {
      if (!dialog._owner) return;
      if (dialog._next && dialog._next.parentNode === dialog._owner) {
        dialog._owner.insertBefore(dialog, dialog._next);
      } else {
        dialog._owner.appendChild(dialog);
      }
      dialog._owner = null;
      dialog._next  = null;
    }
  }

  function setup(section) {
    if (section._sixCardsReady) return; // idempotent
    const viewport = section.querySelector('[data-viewport]');
    const track    = section.querySelector('[data-track]');
    const dotsWrap = section.querySelector('[data-dots]');
    const prevBtn  = section.querySelector('.yr-sixcards__prev');
    const nextBtn  = section.querySelector('.yr-sixcards__next');
    const slides   = Array.from(section.querySelectorAll('.yr-sixcards__slide'));
    if (!viewport || !track || !dotsWrap || !prevBtn || !nextBtn || slides.length === 0) return;

    // ----- slider state -----
    let per = perView();
    track.style.setProperty('--per', per);
    let pages = Math.max(1, Math.ceil(slides.length / per));
    let page = 0;

    function renderDots() {
      dotsWrap.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'yr-sixcards__dot';
        b.setAttribute('aria-label', `Go to page ${i + 1}`);
        if (i === page) b.setAttribute('aria-current', 'true');
        b.addEventListener('click', () => go(i));
        dotsWrap.appendChild(b);
      }
    }

    function updateUI() {
      prevBtn.disabled = page === 0;
      nextBtn.disabled = page >= pages - 1;
      dotsWrap.querySelectorAll('.yr-sixcards__dot').forEach((dot, i) => {
        if (i === page) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
    }

    function go(i) {
      page = Math.max(0, Math.min(pages - 1, i));
      viewport.scrollTo({ left: viewport.clientWidth * page, behavior: 'smooth' });
      updateUI();
    }

    // ----- controls -----
    prevBtn.addEventListener('click', (e) => { e.preventDefault(); go(page - 1); });
    nextBtn.addEventListener('click', (e) => { e.preventDefault(); go(page + 1); });

    viewport.tabIndex = 0;
    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(page + 1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); go(page - 1); }
    });

    // sync on manual scroll
    let t;
    viewport.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const p = Math.round(viewport.scrollLeft / viewport.clientWidth);
        if (p !== page) { page = p; updateUI(); }
      }, 80);
    }, { passive: true });

    // handle resize
    let rT;
    window.addEventListener('resize', () => {
      clearTimeout(rT);
      rT = setTimeout(() => {
        const newPer = perView();
        if (newPer !== per) {
          per = newPer;
          track.style.setProperty('--per', per);
          pages = Math.max(1, Math.ceil(slides.length / per));
          page = Math.min(page, pages - 1);
          renderDots();
          updateUI();
        }
        viewport.scrollTo({ left: viewport.clientWidth * page });
      }, 120);
    });

    // ----- NEW: capture-phase handler to portal the dialog BEFORE other click handlers run -----
    section.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-modal-target]');
      if (!trigger || !section.contains(trigger)) return;
      const sel = trigger.getAttribute('data-modal-target');
      if (!sel) return;
      const dialog = section.querySelector(sel) || document.querySelector(sel);
      if (!dialog) return;
      portalize(dialog, true); // move to <body> before four-cards modal code runs
    }, true); // capture phase

    // When the dialog is closed (close button), move it back
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.yr-fourcards__dlg [data-close]');
      if (!closeBtn) return;
      const dlg = closeBtn.closest('.yr-fourcards__dlg');
      // give other JS time to hide it
      setTimeout(() => portalize(dlg, false), 0);
    });

    // Also restore on ESC (in case close button isn’t used)
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openDlg = document.querySelector('.yr-fourcards__dlg[data-open="true"]');
      if (openDlg) setTimeout(() => portalize(openDlg, false), 0);
    });

    // init
    renderDots();
    updateUI();
    viewport.scrollLeft = 0;
    section._sixCardsReady = true;
  }

  function initAll() {
    document.querySelectorAll(SEL_SECTION).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // keep resilient to dynamic section changes
  const mo = new MutationObserver(() => initAll());
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
